import React, { useState, useRef, ReactElement, useEffect, SetStateAction } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MediaQuery from 'react-responsive';
import BarcodeReader from 'react-barcode-reader';
import { useTranslation } from 'react-i18next';

import Tabs, { Tab } from '../../../layout/Tabs';
import DetailsViewCard from '../../../cards/DetailsViewCard';
import DetailsIcon from '../../../../assets/icons/DetailsIcon';
import ListIcon from '../../../../assets/icons/ListIcon';
import PickItemList from './PickItemList';
import PickTourFooter from './PickTourFooter';
import ManualEntryScreen from '../../../layout/ManualEntryScreen';
import SlideUpFooter from '../../../layout/SlideUpFooter';
import Button from '../../../buttons/Button';
import TextInputScanner from '../../../scanners/TextInputScanner';
import AdjustRecallQuantity from './AdjustRecallQuantity';

import { version } from '../../../../../package.json';

import {
  OF_FLAG_OFF,
  OF_FLAG_ON,
  MQ_MAX_WIDTH,
  MQ_MIN_WIDTH,
  SHOULD_UPDATE_PICK_TOUR_SUB_STATUS,
  TRUE,
  ORDER_TYPE_COMMERCIAL,
} from '../../../../constants';

import {
  adjustSlotQty,
  incrementActivePickPickedQuantity,
  skipLocation,
  setHasJustScanned,
  updatePickTourItem,
  printRecallReport,
  printDiscrepancyReport,
  updatePickTour,
  setActivePickID,
} from '../../../../redux/actions/activePickTour.actions';
import { createToast } from '../../../../redux/actions/toasts.actions';

import {
  getHasJustScannedFromState,
  selectIsTourMixedValidation,
  getConfigAuthValuesFromState,
  isPickingSequenceUpdateEnabled,
} from '../../../../redux/selectors';

import { PickTour, ManualEntry, SkipReason, Pick, PaginationData } from '../../../../types';
import { UpdateType } from '../../../../redux/actions/activePickTour.types';

import {
  calculateAmountToPick,
  generatePickID,
  stripLeadingZeroes,
  setInputIfDigits,
  sortPicksByLocation,
  determineDeviceType,
  sortisEligibleForPicking,
  findNextClosestPick,
  isRecallOrder,
} from '../../../../utils';
import { getTotalPickedQuantity } from '../../../../utils/pickHelpers';

import PaginationBar from '../../../layout/PaginationBar';

import successMP3 from '../../../../assets/audio/success.mp3';
import errorMP3 from '../../../../assets/audio/error.mp3';

import styles from './ActivePickTour.css';
import useInactivePickTourKickout from '../../../../hooks/useInactivePickTourKickout';

import { ActionTypes as MessagesActionTypes, Messages } from '../../../../redux/reducers/message.reducer';
import { RootState } from '../../../../redux/reducers/rootReducer';

type Props = {
  pickTour: PickTour;
  activePickID: string;
  paginationData: PaginationData;
  manualEntryStatus: ManualEntry;
  isTourComplete: boolean;
  filteredPickList: Pick[];
  closePickTour: (updateType: UpdateType) => void;
  setManualEntryStatus: (value: SetStateAction<ManualEntry>) => void;
  setIsTourComplete: (value: SetStateAction<boolean>) => void;
  handlePageChange: (page: number) => void;
  currentLanguage: string;
  containerInputValue: string;
  setContainerInputValue: (value: SetStateAction<string>) => void;
};

const ActivePickTour = ({
  pickTour,
  activePickID,
  manualEntryStatus,
  paginationData,
  isTourComplete,
  filteredPickList,
  closePickTour,
  setManualEntryStatus,
  setIsTourComplete,
  handlePageChange,
  currentLanguage,
  containerInputValue,
  setContainerInputValue,
}: Props): ReactElement | null => {
  const activePick = pickTour.picks.find(pick => generatePickID(pick) === activePickID);

  const activeUpdatedQuantity =
    activePick && activePick.locations.length > 0
      ? activePick.locations[activePick.locations.length - 1].updatedQuantity
      : null;
  const activeKeepQuantity = activePick ? activePick.keepQuantityOnHand : null;
  const activeOriginalQoH = activePick ? activePick.quantityOnHand : null;
  const activeQuantityOnHand =
    activeUpdatedQuantity || activeUpdatedQuantity === 0 ? activeUpdatedQuantity : activeOriginalQoH;

  const quantityLeft = activePick ? calculateAmountToPick(activePick) - activePick.pickedQuantity : null;

  const initialQtyInputValue = quantityLeft ? quantityLeft.toString() : '';

  const { t } = useTranslation();
  const translationRoot = 'ACTIVE_PICK_TOUR';

  const [activeTab, setActiveTab] = useState<'Details View' | 'List View'>('Details View');
  const [itemInputValue, setItemInputValue] = useState('');
  const [qtyInputValue, setQtyInputValue] = useState(initialQtyInputValue);
  const [lastInputTime, setLastInputTime] = useState(0);
  const [activeIsEligiblePickItem, setActiveIsEligiblePickItem] = useState(activePick);

  const hasJustScanned = useSelector(getHasJustScannedFromState);
  const isTourMixedValidation = useSelector(selectIsTourMixedValidation);
  const configAuthValues = useSelector(getConfigAuthValuesFromState);
  const pickingSequenceUpdateEnabled = useSelector(isPickingSequenceUpdateEnabled);
  const message = useSelector((rootState: RootState) => rootState.message.data);
  const activePickTourData = useSelector((rootState: RootState) => rootState.activePickTour?.data);

  const dispatch = useDispatch();

  const justScannedTimer = useRef<number>();
  const scanInInputTimer = useRef<number>();

  const deviceType = determineDeviceType();
  const isCT40 = deviceType === 'CT40';
  const isTC75 = deviceType === 'TC75';

  const successAudio = new Audio(successMP3);
  const errorAudio = new Audio(errorMP3);

  const MIN_UPC_LENGTH = 12;
  const SCANNER_SPEED = 50;

  const stripSpecialChar = (text: string) => text.replace(/[^A-Z0-9]+/gi, '');

  const activePickIndex = pickTour.picks.findIndex(pick => generatePickID(pick) === activePickID);

  const orderType = activePick?.orderType;

  const isOverstockPickTourComplete = orderType === 'OVERSTOCK' && isTourComplete;

  const INACTIVITY_TIMEOUT = 1000 * 60 * (configAuthValues?.INACTIVITY_TIMEOUT || 10);

  const onActivity = useInactivePickTourKickout(INACTIVITY_TIMEOUT);

  const getReorderedPicks = () => {
    if (activePick) {
      if (activePickIndex > -1) {
        return [...pickTour.picks.slice(activePickIndex), ...pickTour.picks.slice(0, activePickIndex)].sort(
          (a, b) => Number(b.isEligibleForPicking) - Number(a.isEligibleForPicking)
        );
      }
    }
    return null;
  };

  const getRestOfPagePicks = () => {
    if (activePick) {
      if (activePickIndex > -1) {
        return [...pickTour.picks.slice(activePickIndex + 1)].sort(
          (a, b) => Number(b.isEligibleForPicking) - Number(a.isEligibleForPicking)
        );
      }
    }
    return null;
  };

  const reorderedPicks = getReorderedPicks();

  const restOfPagePicks = getRestOfPagePicks();

  const getNextPick = () => {
    if (paginationData.isPaginated && paginationData.nextPick) {
      return (
        restOfPagePicks &&
        restOfPagePicks.find(pick => {
          // For RECALL orders, include ALL items (even with 0 picks)
          if (isRecallOrder(pick.orderType)) {
            return !pick.isSkipped;
          }
          // For other order types, include items with remaining picks
          return !pick.isSkipped && pick.pickedQuantity < calculateAmountToPick(pick);
        })
      );
    }
    return (
      reorderedPicks &&
      reorderedPicks.find(pick => {
        if (generatePickID(pick) === activePickID) return false;

        // For RECALL orders, include ALL items (even with 0 picks)
        if (isRecallOrder(pick.orderType)) {
          return !pick.isSkipped;
        }
        return !pick.isSkipped && pick.pickedQuantity < calculateAmountToPick(pick);
      })
    );
  };

  const getNextSkippedPick = () => {
    if (paginationData.isPaginated) {
      if (isTourComplete) {
        if (paginationData.nextPick) {
          return restOfPagePicks && restOfPagePicks[0];
        }
        return reorderedPicks && reorderedPicks[activePickIndex + 1];
      }
      return null;
    }
    if (isTourComplete) {
      return reorderedPicks && reorderedPicks.find(pick => generatePickID(pick) !== activePickID && pick.isSkipped);
    }
    return pickTour.picks.find(pick => pick.isSkipped);
  };

  const nextPick = getNextPick();

  const nextSkippedPick = getNextSkippedPick();

  /**
   * Helper function to check if all eligible items in the pick tour are done (picked or skipped).
   * Includes manual state tracking for the current pick being processed, since Redux state
   * hasn't updated yet when this check runs.
   *
   * @param picks - Array of all picks in the tour
   * @param currentPick - The current active pick being processed
   * @param manualStateUpdate - Optional state override for current pick
   *   - additionalPickedQuantity: Extra quantity being picked (for handlePick)
   *   - isBeingSkipped: Whether current pick is being skipped (for handleSkip)
   * @returns boolean - true if all eligible items are done
   */
  const checkIfAllEligibleItemsDone = (
    picks: Pick[],
    currentPick: Pick,
    manualStateUpdate?: {
      additionalPickedQuantity?: number;
      isBeingSkipped?: boolean;
    }
  ): boolean => {
    const eligiblePicks = picks.filter(pick => pick.isEligibleForPicking);

    return eligiblePicks.every(pick => {
      const isCurrentPick = generatePickID(pick) === generatePickID(currentPick);

      if (isCurrentPick && manualStateUpdate) {
        // Apply manual state override for current pick
        if (manualStateUpdate.isBeingSkipped) {
          // Current pick is being skipped, treat it as done
          return true;
        }
        if (manualStateUpdate.additionalPickedQuantity !== undefined) {
          // Current pick is being picked, use updated quantity
          const updatedPickedQty = pick.pickedQuantity + manualStateUpdate.additionalPickedQuantity;
          return updatedPickedQty >= calculateAmountToPick(pick) || pick.isSkipped;
        }
      }

      // For all other picks, check normal state
      return pick.pickedQuantity >= calculateAmountToPick(pick) || pick.isSkipped;
    });
  };

  /**
   * Helper function to determine the next pick after an item is picked (completed) using either
   * proximity-based routing (for COMMERCIAL orders with feature flag) or the old routing logic.
   *
   * @param currentPick - The current active pick (passed explicitly to avoid closure issues)
   * @returns The next pick to route to
   */
  const getNextPickAfterItemPicked = (currentPick: Pick): Pick => {
    if (pickingSequenceUpdateEnabled && currentPick.orderType === ORDER_TYPE_COMMERCIAL) {
      // For COMMERCIAL orders with feature flag, use proximity-based routing
      // Exclude current pick by ID (since Redux state hasn't updated yet)
      const proximityNextPick = findNextClosestPick({
        picks: pickTour.picks,
        currentPickID: activePickID,
        excludeCurrentPick: true,
      });

      // If proximity routing finds a pick with locations, use it
      if (proximityNextPick) {
        return proximityNextPick;
      }

      // Fall back to sequential ordering when no items have locations
      // Filter by eligibility to only route to eligible items
      const nextEligiblePick = nextPick && nextPick.isEligibleForPicking ? nextPick : null;
      const nextEligibleSkippedPick = nextSkippedPick && nextSkippedPick.isEligibleForPicking ? nextSkippedPick : null;

      return nextEligiblePick || nextEligibleSkippedPick || currentPick;
    }

    // Old logic for non-commercial or when feature flag is OFF
    // Simple fallback chain: nextPick || nextSkippedPick || currentPick
    const result = nextPick || nextSkippedPick || currentPick;

    return result;
  };

  /**
   * Helper function to determine the next pick after an item is skipped using either
   * proximity-based routing (for COMMERCIAL orders with feature flag) or the old routing logic.
   *
   * @param currentPick - The current active pick (passed explicitly to avoid closure issues)
   * @returns The next pick to route to
   */
  const getNextPickAfterItemSkipped = (currentPick: Pick): Pick => {
    if (pickingSequenceUpdateEnabled && currentPick.orderType === ORDER_TYPE_COMMERCIAL) {
      // For COMMERCIAL orders with feature flag, use proximity-based routing
      // Exclude current pick by ID (since Redux state hasn't updated yet)
      const proximityNextPick = findNextClosestPick({
        picks: pickTour.picks,
        currentPickID: activePickID,
        excludeCurrentPick: true,
      });

      // If proximity routing finds a pick with locations, use it
      if (proximityNextPick) {
        return proximityNextPick;
      }

      // Fall back to sequential ordering when no items have locations
      // Filter by eligibility to only route to eligible items
      if (nextPick && nextPick.isEligibleForPicking) {
        return nextPick;
      }
      if (
        nextSkippedPick &&
        nextSkippedPick.isEligibleForPicking &&
        (isTourComplete ||
          paginationData.isPaginated ||
          pickTour.picks.findIndex(pick => generatePickID(pick) === generatePickID(nextSkippedPick)) <
            pickTour.picks.findIndex(pick => generatePickID(pick) === activePickID))
      ) {
        return nextSkippedPick;
      }
      return currentPick;
    }

    // Old logic for non-commercial or when feature flag is OFF
    // Conditional logic for nextSkippedPick
    if (nextPick) {
      return nextPick;
    }
    if (
      nextSkippedPick &&
      (isTourComplete ||
        paginationData.isPaginated ||
        pickTour.picks.findIndex(pick => generatePickID(pick) === generatePickID(nextSkippedPick)) <
          pickTour.picks.findIndex(pick => generatePickID(pick) === activePickID))
    ) {
      return nextSkippedPick;
    }
    return currentPick;
  };

  const validateManualItemEntry = (itemIdentifier: string) => {
    if (activePick && activePick.pickedQuantity < calculateAmountToPick(activePick)) {
      const activePickItemAlphanumericalIdentifiers = [activePick.partNumber || '', activePick.altPartNumber || '']
        .map(partNumber => stripSpecialChar(partNumber.toLowerCase()))
        .filter(identifier => identifier);

      const activePickItemNumericalIdentifiers = [
        stripLeadingZeroes(activePick.sku),
        ...activePick.upcs.map(upc => stripLeadingZeroes(upc)),
      ].filter(identifier => identifier);

      if (
        activePickItemAlphanumericalIdentifiers.includes(stripSpecialChar(itemIdentifier.toLowerCase())) ||
        activePickItemNumericalIdentifiers.includes(stripLeadingZeroes(itemIdentifier))
      ) {
        return true;
      }
      dispatch(
        createToast({
          type: 'error',
          message: t('ERRORS.DOES_NOT_MATCH'),
          extraData: {
            deviceType,
            version,
            itemId: activePick.sku,
            expectedUPCs: activePick.upcs,
            scannedUPC: itemIdentifier,
          },
        })
      );
      errorAudio.play();
      return false;
    }
    return false;
  };

  const validateManualQuantityEntry = (quantity: number) => {
    if (quantityLeft !== null) {
      if (quantityLeft < 1) {
        dispatch(
          createToast({
            type: 'error',
            message: t('ERRORS.ALREADY_PICKED'),
          })
        );
        errorAudio.play();
        return false;
      }

      if (quantityLeft < 1) {
        dispatch(
          createToast({
            type: 'error',
            message: t('ERRORS.ALREADY_PICKED'),
          })
        );
        errorAudio.play();
        return false;
      }

      if (quantity < 1 || quantity > quantityLeft) {
        dispatch(
          createToast({
            type: 'error',
            message: quantityLeft > 1 ? `Quantity must be between 1 and ${quantityLeft}` : 'Quantity must be 1',
          })
        );
        errorAudio.play();
        return false;
      }
      return true;
    }
    return false;
  };

  const handleAdjustSlotQty = () => {
    if (activePick) {
      // Item needs to be updated during slot adjustment if partially picked
      if (
        activePick.locations.length > 0 && activePick.locations[activePick.locationIndex]
          ? activePick.locations[activePick.locationIndex].pickedQuantity > 0
          : activePick.pickedQuantity > 0
      ) {
        // Calculate new picks from current session (sum of all location quantities)
        const newPicksFromLocations = getTotalPickedQuantity(activePick.locations);

        dispatch(
          updatePickTourItem({
            pickID: activePickID,
            pickedQuantity: newPicksFromLocations,
            skipReason: null,
            isSkipped: false,
            locationIndex: activePick.locationIndex,
            nextPick: nextPick || nextSkippedPick || activePick,
            paginationData,
            isLastPick: !nextPick,
          })
        );
      } else {
        dispatch(
          adjustSlotQty({
            sku: activePick.sku,
            slotID: activePick.locations[0].itemSequence,
            slotQty: activePick.locations[0].slotQuantity || 0,
            pickTourID: pickTour.id,
            pickTourItemDetailLineNumber: activePick.lineNumber,
          })
        );
      }
    }
  };

  const handleNonPick = (nonPickTourList: Pick[], pickType: string) => {
    nonPickTourList.forEach((item, index) => {
      dispatch(
        updatePickTourItem({
          pickID: generatePickID(item),
          pickedQuantity: calculateAmountToPick(item) - item.pickedQuantity,
          skipReason: null,
          isSkipped: false,
          locationIndex: item.locationIndex + index,
          nextPick: item,
          paginationData,
          isLastPick: nonPickTourList.length === index + 1,
        })
      );
    });

    if (pickType === OF_FLAG_OFF) {
      closePickTour('finish');
    }
  };

  const handlePick = (quantity: number) => {
    // Allow picking in two cases:
    // 1. Standard: item is eligible for picking (all order types)
    // 2. RECALL-specific: RECALL item with 0 picks (allows completing items where QOH <= Keep)
    const amountToPick = activePick ? calculateAmountToPick(activePick) : 0;
    const canPick =
      activePick && (activePick.isEligibleForPicking || (isRecallOrder(activePick.orderType) && amountToPick === 0));

    if (canPick) {
      setManualEntryStatus('closed');
      dispatch(incrementActivePickPickedQuantity({ quantity }));
      dispatch(setHasJustScanned({ hasJustScanned: true }));
      successAudio.play();
      // Clear any existing timer before setting a new one to prevent multiple dispatches
      if (justScannedTimer.current) {
        window.clearTimeout(justScannedTimer.current);
      }
      // Timer: 500ms delay to allow success audio to play and provide brief UI feedback
      // before updating the pick state and checking for non-pickable items
      justScannedTimer.current = window.setTimeout(() => {
        const newPickedQuantity = activePick.pickedQuantity + quantity;
        const totalAmountToPick = calculateAmountToPick(activePick);
        const isFullyPicked = newPickedQuantity === totalAmountToPick;

        if (activePick && isFullyPicked) {
          const nextActivePick = getNextPickAfterItemPicked(activePick);

          dispatch(
            updatePickTourItem({
              pickID: activePickID,
              pickedQuantity: getTotalPickedQuantity(activePick.locations) + quantity,
              skipReason: null,
              isSkipped: false,
              locationIndex: activePick.locationIndex,
              nextPick: nextActivePick,
              paginationData,
              isLastPick: !nextPick,
            })
          );

          // FIX: For COMMERCIAL orders with proximity routing enabled, check if all eligible items are done
          if (pickingSequenceUpdateEnabled && activePick.orderType === ORDER_TYPE_COMMERCIAL) {
            const sortedPickTourList = sortisEligibleForPicking(pickTour);
            const nonPickableList = sortedPickTourList.filter(pick => !pick.isEligibleForPicking);

            // Check if all eligible items are done (fully picked or skipped)
            // NOTE: We manually check current item as "picked" since Redux state hasn't updated yet
            const allEligibleItemsDone = checkIfAllEligibleItemsDone(pickTour.picks, activePick, {
              additionalPickedQuantity: quantity,
            });

            if (allEligibleItemsDone && nonPickableList && nonPickableList.length > 0) {
              setActiveIsEligiblePickItem(activePick);
              handleNonPick(nonPickableList, OF_FLAG_ON);
            }
          }
        } else {
          dispatch(setHasJustScanned({ hasJustScanned: false }));
          if (isTourComplete) {
            setIsTourComplete(false);
          }
        }
      }, 500);

      // Original old logic: Runs OUTSIDE timeout for when flag is OFF or non-COMMERCIAL orders
      // This preserves the original behavior for SAT2SAT, OVERSTOCK, RECALL, and COMMERCIAL when flag is OFF
      if (!pickingSequenceUpdateEnabled || activePick.orderType !== ORDER_TYPE_COMMERCIAL) {
        const sortedPickTourList = sortisEligibleForPicking(pickTour);
        const nonPickableList = sortedPickTourList.filter(pick => !pick.isEligibleForPicking);

        if (
          nextPick &&
          !nextPick.isEligibleForPicking &&
          nonPickableList &&
          activePick.pickedQuantity + quantity === calculateAmountToPick(activePick)
        ) {
          setActiveIsEligiblePickItem(activePick);
          handleNonPick(nonPickableList, OF_FLAG_ON);
        }
      }
    }
  };

  const handleSkip = (skipReason: SkipReason) => {
    if (activePick) {
      // For RECALL orders, just navigate to next item without sending skip to backend
      // Backend doesn't support skip functionality for RECALL orders
      if (isRecallOrder(activePick.orderType)) {
        // Find next RECALL item on current page
        const currentIndex = pickTour.picks.findIndex(p => generatePickID(p) === activePickID);
        const nextRecallItemOnPage = pickTour.picks.find((p, idx) => idx > currentIndex && isRecallOrder(p.orderType));

        if (nextRecallItemOnPage) {
          // Found another RECALL item on current page
          dispatch(setActivePickID({ id: generatePickID(nextRecallItemOnPage) }));
        } else if (paginationData.isPaginated && paginationData.pageNumber < paginationData.totalPages - 1) {
          // No more RECALL items on current page - navigate to next sequential page
          // Use sequential navigation instead of relying on nextPickPageNumber since backend
          // doesn't track RECALL item resolution (we don't send skip updates for RECALL)
          const nextPageNumber = paginationData.pageNumber + 2; // +2 because pageNumber is 0-indexed, handlePageChange expects 1-indexed
          handlePageChange(nextPageNumber);
        }
        // If no more pages, do nothing - let isFullyPickedOrSkipped calculation in PickTourFlow.tsx
        // determine when tour is truly complete and enable finish button
        return; // Don't call updatePickTourItem for RECALL skips
      }

      // For non-RECALL orders, use the standard skip logic
      const nextActivePick = getNextPickAfterItemSkipped(activePick);

      // For non-pickable items, just navigate to next item without sending skip to backend
      // Backend doesn't accept skip status for non-pickable items (returns INVALID_SKIP_STATUS)
      if (activePick.isEligibleForPicking === false) {
        dispatch(setActivePickID({ id: generatePickID(nextActivePick) }));

        // Check if next item is also non-pickable and auto-complete if needed
        const sortedPickTourList = sortisEligibleForPicking(pickTour);
        const nonPickableList = sortedPickTourList.filter(pick => !pick.isEligibleForPicking);

        if (nextActivePick && !nextActivePick.isEligibleForPicking && nonPickableList) {
          setActiveIsEligiblePickItem(activePick);
          handleNonPick(nonPickableList, OF_FLAG_ON);
        }
        return; // Don't call updatePickTourItem for non-pickable items
      }

      // Calculate new picks from current session (for partial picks before skip)
      const newPicksFromLocations = getTotalPickedQuantity(activePick.locations);

      dispatch(
        updatePickTourItem({
          pickID: activePickID,
          pickedQuantity: newPicksFromLocations,
          skipReason,
          isSkipped: true,
          locationIndex: activePick.locationIndex,
          nextPick: nextActivePick,
          paginationData,
          isLastPick: !nextPick,
        })
      );

      const sortedPickTourList = sortisEligibleForPicking(pickTour);
      const nonPickableList = sortedPickTourList.filter(pick => !pick.isEligibleForPicking);

      if (pickingSequenceUpdateEnabled && activePick.orderType === ORDER_TYPE_COMMERCIAL) {
        // For COMMERCIAL orders with proximity routing: check if all eligible items are done
        // NOTE: We manually check current item as "skipped" since Redux state hasn't updated yet
        const allEligibleItemsDone = checkIfAllEligibleItemsDone(pickTour.picks, activePick, {
          isBeingSkipped: true,
        });

        if (allEligibleItemsDone && nonPickableList && nonPickableList.length > 0) {
          setActiveIsEligiblePickItem(activePick);
          handleNonPick(nonPickableList, OF_FLAG_ON);
        }
      } else if (nextPick && !nextPick.isEligibleForPicking && nonPickableList) {
        // Old logic for SAT2SAT, OVERSTOCK, RECALL, and COMMERCIAL when flag is OFF
        setActiveIsEligiblePickItem(activePick);
        handleNonPick(nonPickableList, OF_FLAG_ON);
      }
    }
  };

  const handleAdjustRecallQty = () => {
    setManualEntryStatus('adjustRecall');
  };

  const handleScan = (value: string) => {
    onActivity();
    let modifiedValue = value;
    if (modifiedValue.length === 8) {
      const seventhDigit = Number(modifiedValue.charAt(6));
      switch (seventhDigit) {
        case 0:
        case 1:
        case 2:
          modifiedValue = `${modifiedValue.substring(0, 3) + seventhDigit}0000${modifiedValue.substring(
            3,
            6
          )}${modifiedValue.substring(7)}`;
          break;
        case 3:
          modifiedValue = `${modifiedValue.substring(0, 4)}00000${modifiedValue.substring(
            4,
            6
          )}${modifiedValue.substring(7)}`;
          break;
        case 4:
          modifiedValue = `${modifiedValue.substring(0, 5)}00000${modifiedValue.substring(
            5,
            6
          )}${modifiedValue.substring(7)}`;
          break;
        default:
          modifiedValue = `${modifiedValue.substring(0, 6)}0000${modifiedValue.substring(6)}`;
      }
    }
    if (isOverstockPickTourComplete) {
      // TODO: Toast about finishing tour here?
    } else if (validateManualItemEntry(modifiedValue)) {
      switch (manualEntryStatus) {
        case 'closed': {
          handlePick(1);
          break;
        }
        case 'item': {
          handlePick(parseInt(qtyInputValue, 10));
          break;
        }
        default: {
          errorAudio.play();
          dispatch(createToast({ type: 'error', message: t('ERRORS.MUST_SCAN_FROM_DETAILS') }));
          break;
        }
      }
    }
  };

  const handleManualEntrySubmit = () => {
    if (validateManualItemEntry(itemInputValue) && validateManualQuantityEntry(parseInt(qtyInputValue, 10))) {
      handlePick(parseInt(qtyInputValue, 10));
    }
  };

  const handleCompleteWithQtyOnly = () => {
    if (validateManualQuantityEntry(parseInt(qtyInputValue, 10))) {
      handlePick(parseInt(qtyInputValue, 10));
    }
  };

  const handleSkipLocation = () => {
    if (activePick) {
      if (pickingSequenceUpdateEnabled && activePick.orderType === ORDER_TYPE_COMMERCIAL) {
        // New proximity-based logic for COMMERCIAL orders
        // Step 1: Advance locationIndex for active pick AND all same-SKU picks that are not fully picked and not skipped
        const updatedPicks = pickTour.picks.map(pick => {
          const isSameSKU = pick.sku === activePick.sku;
          const hasNextLocation = pick.locations.length > pick.locationIndex + 1;
          const isNotFullyPicked = pick.pickedQuantity < calculateAmountToPick(pick);
          const isNotSkipped = !pick.isSkipped;

          if (
            (generatePickID(pick) === activePickID || (isSameSKU && isNotFullyPicked && isNotSkipped)) &&
            hasNextLocation
          ) {
            return {
              ...pick,
              locations: pick.locations.map((location, index) =>
                index === pick.locationIndex + 1
                  ? {
                      ...location,
                      isVisible: true,
                    }
                  : location
              ),
              locationIndex: pick.locationIndex + 1,
            };
          }
          return pick;
        });

        // Step 2: Find next closest pick by proximity (with tie-breaker for current item)
        const updatedNextPick = findNextClosestPick({ picks: updatedPicks, currentPickID: activePickID }) || activePick;

        if (generatePickID(activePick) !== generatePickID(updatedNextPick)) {
          dispatch(
            createToast({
              type: 'information',
              message: t(`${translationRoot}.PROCEED_TO_NEXT_ITEM`),
            })
          );
        }
        dispatch(skipLocation({ updatedPicks, updatedNextPick }));

        // Check for non-pickable items after skipping location
        // ONLY trigger auto-complete if:
        // 1. Current item has NO more locations (all locations visited)
        // 2. Current item is DONE (fully picked or skipped)
        // 3. No other eligible items have valid locations

        const currentItemInUpdatedPicks = updatedPicks.find(
          pick => generatePickID(pick) === generatePickID(activePick)
        );
        const currentItemHasMoreLocations =
          currentItemInUpdatedPicks &&
          currentItemInUpdatedPicks.locations.length > currentItemInUpdatedPicks.locationIndex;

        if (!currentItemHasMoreLocations && currentItemInUpdatedPicks) {
          const currentItemIsDone =
            currentItemInUpdatedPicks.pickedQuantity >= calculateAmountToPick(currentItemInUpdatedPicks) ||
            currentItemInUpdatedPicks.isSkipped;

          if (currentItemIsDone) {
            const otherEligibleItemsWithLocations = updatedPicks.some(
              pick =>
                pick.isEligibleForPicking &&
                generatePickID(pick) !== generatePickID(activePick) &&
                pick.locations.length > pick.locationIndex &&
                pick.pickedQuantity < calculateAmountToPick(pick) &&
                !pick.isSkipped
            );

            if (!otherEligibleItemsWithLocations) {
              const sortedPickTourList = sortisEligibleForPicking({ picks: updatedPicks } as PickTour);
              const nonPickableList = sortedPickTourList.filter(pick => !pick.isEligibleForPicking);

              if (nonPickableList && nonPickableList.length > 0) {
                setActiveIsEligiblePickItem(activePick);
                handleNonPick(nonPickableList, OF_FLAG_ON);
              }
            }
          }
        }
      } else {
        // Old logic: For non-commercial orders or when feature flag is OFF
        const updatedPicks = sortPicksByLocation({
          picks: pickTour.picks.map(pick =>
            generatePickID(pick) === activePickID
              ? {
                  ...pick,
                  locations: pick.locations.map((location, index) =>
                    index === pick.locationIndex + 1
                      ? {
                          ...location,
                          isVisible: true,
                        }
                      : location
                  ),
                  locationIndex: pick.locationIndex + 1,
                }
              : pick
          ),
        });
        const updatedPicksWithOldPick = sortPicksByLocation({ picks: [...updatedPicks, activePick] });
        const updatedNextPick =
          updatedPicksWithOldPick
            .slice(updatedPicksWithOldPick.findIndex(pick => generatePickID(pick) === activePickID) + 1)
            .find(pick => pick.pickedQuantity < calculateAmountToPick(pick) && !pick.isSkipped) || activePick;
        if (generatePickID(activePick) !== generatePickID(updatedNextPick)) {
          dispatch(
            createToast({
              type: 'information',
              message: t(`${translationRoot}.PROCEED_TO_NEXT_ITEM`),
            })
          );
        }
        dispatch(skipLocation({ updatedPicks, updatedNextPick }));
      }
    }
  };

  const detailsViewCardWithProps =
    hasJustScanned !== null && isTourMixedValidation !== null ? (
      <DetailsViewCard
        itemInputValue={itemInputValue}
        qtyInputValue={qtyInputValue}
        isTourMixedValidation={isTourMixedValidation}
        currentLanguage={currentLanguage}
        setItemInputValue={setItemInputValue}
        setQtyInputValue={setQtyInputValue}
        activePick={(() => {
          const actualActivePick = pickTour.picks.find(pick => generatePickID(pick) === activePickID);

          // For RECALL orders, always show the actual active pick (even if not eligible for picking)
          // This allows users to see and interact with all RECALL items, including 0-pick items
          if (actualActivePick && isRecallOrder(actualActivePick.orderType)) {
            return actualActivePick;
          }

          // For non-RECALL orders, use the original logic:
          // Find eligible pick, or fall back to activeIsEligiblePickItem or first pick
          const foundPick = pickTour.picks.find(
            pick => generatePickID(pick) === activePickID && pick.isEligibleForPicking
          );

          return foundPick || activeIsEligiblePickItem || pickTour.picks[0];
        })()}
        hasJustScanned={hasJustScanned}
        isTourComplete={isTourComplete}
        onManualEntrySubmit={handleManualEntrySubmit}
        handlePick={handlePick}
        onCompleteWithQtyOnly={handleCompleteWithQtyOnly}
        onSkip={handleSkip}
        setShouldShowManualEntry={setManualEntryStatus}
        skipLocation={handleSkipLocation}
        handleAdjustSlotQty={handleAdjustSlotQty}
        handleAdjustRecallQty={handleAdjustRecallQty}
        quantityOnHand={activeQuantityOnHand}
        keepQuantity={activeKeepQuantity}
      />
    ) : null;

  const FinishFooter = (): ReactElement => (
    <SlideUpFooter className={styles.finishFooter} isOpen={isTourComplete}>
      <Button
        onClick={() => {
          if (isRecallOrder(orderType)) {
            setManualEntryStatus('labels');
          } else {
            closePickTour('finish');
          }
        }}
      >
        {t(`${translationRoot}.PICK_TOUR_FOOTER.FINISH_TOUR_BUTTON`)}
      </Button>
    </SlideUpFooter>
  );

  const containerInputScreen = (): ReactElement => (
    <ManualEntryScreen
      buttonText={t(`${translationRoot}.DETAILS_VIEW.CONFIRM`)}
      disabled={containerInputValue === ''}
      inputValue={containerInputValue}
      type="number"
      handleInputChange={value => setInputIfDigits({ inputSetter: setContainerInputValue, value })}
      label={t(`${translationRoot}.DETAILS_VIEW.QTY_INPUT_MOBILE.PLACEHOLDER`)}
      memo={t(`${translationRoot}.DETAILS_VIEW.PLEASE_ENTER_CONTAINERS`)}
      placeholder={t(`${translationRoot}.DETAILS_VIEW.QTY_INPUT_MOBILE.PLACEHOLDER`)}
      additionalMemo={t(`${translationRoot}.DETAILS_VIEW.RECALL_STICKER_WARNING_MESSAGE`)}
      handleSubmit={() => {
        if (parseInt(containerInputValue, 10) > 0) {
          setManualEntryStatus('closed');
          closePickTour('finish');
        } else if (parseInt(containerInputValue, 10) === 0 && pickTour.picks.every(pick => pick.pickedQuantity === 0)) {
          setManualEntryStatus('closed');
          closePickTour('finish');
        } else {
          dispatch(
            createToast({
              type: 'error',
              message: t(`${translationRoot}.DETAILS_VIEW.LABEL_VALIDATION`),
            })
          );
        }
      }}
    />
  );

  const mobileView = (): ReactElement => {
    const manualEntryScreenHeader: ReactElement = (
      <header className={styles.manualEntryHeader}>
        <p>{t(`${translationRoot}.DETAILS_VIEW.PART_NUMBER`)}</p>
        <h1 data-cy="manual-entry-part-number">{activePick && activePick.partNumber}</h1>
      </header>
    );
    if (manualEntryStatus === 'item') {
      const handleItemSubmit = (currentValue: string) => {
        if (validateManualItemEntry(currentValue)) {
          handlePick(parseInt(qtyInputValue, 10));
        }
      };
      return (
        <ManualEntryScreen
          buttonText={t(`${translationRoot}.DETAILS_VIEW.CONFIRM`)}
          disabled={itemInputValue === ''}
          inputValue={itemInputValue}
          handleInputChange={value => {
            setItemInputValue(value);
            if (isCT40 && value.length >= MIN_UPC_LENGTH - 1) {
              const currentTime = Date.now();
              if (value.length >= MIN_UPC_LENGTH && currentTime - lastInputTime < SCANNER_SPEED) {
                if (scanInInputTimer.current !== undefined) {
                  window.clearTimeout(scanInInputTimer.current);
                }
                scanInInputTimer.current = window.setTimeout(() => handleItemSubmit(value), SCANNER_SPEED);
              }
              setLastInputTime(currentTime);
            }
          }}
          label={t(`${translationRoot}.DETAILS_VIEW.PART_NUMBER_INPUT_MOBILE.PLACEHOLDER`)}
          memo={t(`${translationRoot}.DETAILS_VIEW.PART_NUMBER_INPUT_MOBILE.MESSAGE`)}
          placeholder={t(`${translationRoot}.DETAILS_VIEW.PART_NUMBER_INPUT_MOBILE.PLACEHOLDER`)}
          handleSubmit={() => handleItemSubmit(itemInputValue)}
        >
          {manualEntryScreenHeader}
        </ManualEntryScreen>
      );
    }
    if (manualEntryStatus === 'quantity') {
      return (
        <ManualEntryScreen
          buttonText={t(
            `${translationRoot}.DETAILS_VIEW.${
              activePick?.isUpcScanRequired || isTourMixedValidation ? 'NEXT' : 'CONFIRM'
            }`
          )}
          disabled={qtyInputValue === ''}
          inputValue={qtyInputValue}
          type="number"
          handleInputChange={value => setInputIfDigits({ inputSetter: setQtyInputValue, value })}
          label={t(`${translationRoot}.DETAILS_VIEW.QTY_INPUT_MOBILE.PLACEHOLDER`)}
          memo={t(`${translationRoot}.DETAILS_VIEW.QTY_INPUT_MOBILE.MESSAGE`)}
          placeholder={t(`${translationRoot}.DETAILS_VIEW.QTY_INPUT_MOBILE.PLACEHOLDER`)}
          handleSubmit={() => {
            if (validateManualQuantityEntry(parseInt(qtyInputValue, 10))) {
              if (activePick?.isUpcScanRequired || isTourMixedValidation) {
                setManualEntryStatus('item');
              } else {
                handlePick(parseInt(qtyInputValue, 10));
              }
            }
          }}
        >
          {manualEntryScreenHeader}
        </ManualEntryScreen>
      );
    }
    if (manualEntryStatus === 'labels') {
      return containerInputScreen();
    }
    return (
      <Tabs activeTab={activeTab} setActiveTab={label => setActiveTab(label)}>
        <Tab
          icon={<DetailsIcon />}
          displayName={t(`${translationRoot}.DETAILS_VIEW.DETAILS_VIEW_TAB`)}
          label="Details View"
          data-cy="details-view-tab"
        >
          {detailsViewCardWithProps}
          {FinishFooter()}
          <PickTourFooter pick={nextPick || (paginationData.isPaginated ? paginationData.nextPick : null)} />
        </Tab>
        <Tab
          icon={<ListIcon />}
          displayName={t(`${translationRoot}.LIST_VIEW.LIST_VIEW_TAB`)}
          label="List View"
          data-cy="list-view-tab"
        >
          <PickItemList
            picks={filteredPickList}
            isTourComplete={isTourComplete}
            isActivePickTourPage
            isPaginated={paginationData.isPaginated}
            setActiveTab={setActiveTab}
          />
          {paginationData.isPaginated && (
            <PaginationBar
              isDisabled={!isTourComplete}
              currentPage={paginationData.pageNumber + 1}
              totalPages={paginationData.totalPages}
              onPageChange={handlePageChange}
            />
          )}
          {FinishFooter()}
        </Tab>
      </Tabs>
    );
  };

  const desktopView = (): ReactElement => {
    if (manualEntryStatus === 'labels') {
      return containerInputScreen();
    }
    return (
      <section className={styles.section}>
        <header className={styles.header}>
          <DetailsIcon />
          <h2>{t(`${translationRoot}.DETAILS_VIEW.DETAILS_VIEW_TAB`)}</h2>
        </header>
        {detailsViewCardWithProps}
        {FinishFooter()}
      </section>
    );
  };

  useEffect(() => {
    /**
     * Pick Tour Status Update Logic - Two Different Flows:
     *
     * FLOW 1: Selective Resume (Edit Order scenario)
     * - Triggered when: Resuming a bundled commercial pick tour with multiple orders AND editOrderEnabled = true
     * - What happens: The resumePickTourWithOrderSelection saga handles the PUT request to update
     *   the pick tour status to PROGRESS along with the filtered order selection
     * - Result: Pick tour is already in PROGRESS state when this component loads
     * - Action here: Skip the PUT request to avoid redundant API calls and potential conflicts
     *
     * FLOW 2: Normal Resume (Regular scenario)
     * - Triggered when: Resuming any other pick tour (single order, non-commercial, or editOrderEnabled = false)
     * - What happens: OrderList sets localStorage flag SHOULD_UPDATE_PICK_TOUR_SUB_STATUS = TRUE,
     *   then navigates to ActivePickTour via getPickTourItems
     * - Result: Pick tour is still in PAUSED state when this component loads
     * - Action here: Send PUT request to update pick tour status from PAUSED to PROGRESS
     *
     * The localStorage flag distinguishes between these two flows, and checking isPickTourAlreadyInProgress
     * prevents duplicate PUT requests in case the saga has already updated the status.
     */
    const isPickTourAlreadyInProgress = activePickTourData?.pickTourSubStatus === 'PROGRESS';

    const updatePickTourSubStatus = localStorage.getItem(SHOULD_UPDATE_PICK_TOUR_SUB_STATUS) === TRUE;
    localStorage.removeItem(SHOULD_UPDATE_PICK_TOUR_SUB_STATUS);

    if (updatePickTourSubStatus && !isPickTourAlreadyInProgress) {
      dispatch(
        updatePickTour({
          updateType: 'status',
          updateStatus: { pickTourStatus: 'OPEN', pickTourSubStatus: 'PROGRESS' },
        })
      );
    }
  }, [dispatch, activePickTourData]);

  useEffect(() => {
    setItemInputValue('');
    setQtyInputValue(initialQtyInputValue);
  }, [activePickID, initialQtyInputValue]);

  useEffect(() => {
    if (isTourComplete && orderType !== 'OVERSTOCK') {
      setActiveTab('List View');
    }
  }, [isTourComplete, orderType]);

  // effect to listen to messages
  useEffect(() => {
    if (message === Messages.PRINT_RECALL_REPORT) {
      dispatch(
        printRecallReport({
          pickTourId: pickTour.id,
          labelQty: parseInt(containerInputValue, 10),
        })
      );
      dispatch({ type: MessagesActionTypes.CLEAR_MESSAGE });
    }
    if (message === Messages.PRINT_DISCREPANCY_REPORT) {
      dispatch(
        printDiscrepancyReport({
          storeId: activePick && activePick.destination ? activePick.destination : '',
        })
      );
      dispatch({ type: MessagesActionTypes.CLEAR_MESSAGE });
    }
  }, [activePick, containerInputValue, dispatch, message, pickTour.id]);

  useEffect(() => {
    const checkisNotEligibleForPicking = pickTour.picks.every(pick => !pick.isEligibleForPicking);
    if (checkisNotEligibleForPicking) {
      handleNonPick(pickTour.picks, OF_FLAG_OFF);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup timers on unmount to prevent memory leaks and infinite API loops
  useEffect(() => {
    return () => {
      if (justScannedTimer.current) {
        window.clearTimeout(justScannedTimer.current);
      }
      if (scanInInputTimer.current) {
        window.clearTimeout(scanInInputTimer.current);
      }
    };
  }, []);

  return activePickID !== null ? (
    <>
      {manualEntryStatus === 'adjustRecall' ? (
        <AdjustRecallQuantity
          partNumber={(activePick && activePick.partNumber) || ''}
          systemQoH={activeQuantityOnHand || 0}
          keepQoH={activeKeepQuantity || 0}
          amountScanned={activePick ? activePick.pickedQuantity : 0}
          handleConfirmQoH={updatedQuantity => {
            if (activePick) {
              // Calculate new picks from current session
              const newPicksFromLocations = getTotalPickedQuantity(activePick.locations);

              dispatch(
                updatePickTourItem({
                  pickID: activePickID,
                  pickedQuantity: newPicksFromLocations,
                  skipReason: null,
                  isSkipped: false,
                  locationIndex: activePick.locationIndex,
                  nextPick: nextPick || nextSkippedPick || activePick,
                  paginationData,
                  isLastPick: !nextPick,
                  updatedQuantity,
                })
              );
            }
            setManualEntryStatus('closed');
          }}
          handleConfirmAmount={updatedQuantity => {
            if (activePick) {
              dispatch(
                updatePickTourItem({
                  pickID: activePickID,
                  pickedQuantity: 0,
                  skipReason: null,
                  isSkipped: false,
                  locationIndex: activePick.locationIndex,
                  nextPick: nextPick || nextSkippedPick || activePick,
                  paginationData,
                  isLastPick: !nextPick,
                  updatedQuantity,
                })
              );
            }
            setManualEntryStatus('closed');
          }}
        />
      ) : (
        <>
          <MediaQuery maxWidth={MQ_MAX_WIDTH}>{mobileView()}</MediaQuery>
          <MediaQuery minWidth={MQ_MIN_WIDTH}>{desktopView()}</MediaQuery>
        </>
      )}
      {isCT40 && <BarcodeReader onScan={(value: string) => handleScan(value)} avgTimeByChar={50} />}
      {isTC75 && <TextInputScanner onScan={value => handleScan(value)} />}
    </>
  ) : null;
};

export default ActivePickTour;
